import React, { useEffect, useState } from "react";
import { Badge, Box, Button, Flex, SimpleGrid, Spinner, Text, useToast } from "@chakra-ui/react";
import { PiKeyDuotone as FiKey, PiCopyDuotone as FiCopy, PiDownloadSimpleDuotone as FiDownload, PiLifebuoyDuotone as FiLifeBuoy } from "react-icons/pi";
import { getRecoveryStatus, generateRecoveryCodes } from "@lubbezposrednio-frontend/core";
import { SecurityCard } from "./SecurityCard";

const ACCENT = "#D97706";

export const RecoveryCodesCard: React.FC = () => {
  const toast = useToast();
  const [remaining, setRemaining] = useState<number | null>(null);
  const [generated, setGenerated] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null); // pokazywane RAZ po wygenerowaniu
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getRecoveryStatus();
        setRemaining(res.data.remaining);
        setGenerated(res.data.generated);
      } catch {
        setRemaining(0);
      }
    })();
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await generateRecoveryCodes();
      setCodes(res.data.codes);
      setRemaining(res.data.remaining);
      setGenerated(true);
    } catch {
      toast({ title: "Nie udało się wygenerować kodów.", status: "error", isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  const copyAll = () => {
    if (!codes) return;
    navigator.clipboard.writeText(codes.join("\n"));
    toast({ title: "Skopiowano kody.", status: "success", isClosable: true });
  };

  const download = () => {
    if (!codes) return;
    const blob = new Blob([`Kody zapasowe lubbezpośrednio\n\n${codes.join("\n")}\n\nKażdy kod działa raz. Trzymaj je w bezpiecznym miejscu.`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "lubbezposrednio-kody-zapasowe.txt";
    a.click();
  };

  return (
    <SecurityCard
      icon={FiLifeBuoy}
      accent={ACCENT}
      full
      title="Kody zapasowe"
      subtitle="Jednorazowe kody na wypadek utraty telefonu, aplikacji authenticator i dostępu do e-maila. Każdy działa raz."
      badge={
        remaining !== null && generated ? (
          <Badge bg={remaining > 0 ? "rgba(217,119,6,0.12)" : "rgba(229,31,68,0.12)"} color={remaining > 0 ? "#B45309" : "#E51F44"} borderRadius="6px">
            pozostało {remaining}
          </Badge>
        ) : undefined
      }
    >
      {codes ? (
        <Box mt="16px">
          <Box bg="rgba(229,31,68,0.06)" border="1px solid rgba(229,31,68,0.2)" borderRadius="12px" p="12px 14px" mb="14px">
            <Text fontSize="13px" color="#7A1020" fontWeight="600">
              Zapisz te kody teraz — pokazujemy je tylko raz. Wygenerowanie nowych unieważnia poprzednie.
            </Text>
          </Box>
          <SimpleGrid columns={{ base: 2, md: 5 }} spacing="10px">
            {codes.map(c => (
              <Box key={c} fontFamily="mono" fontWeight="800" fontSize="15px" color="#B45309" textAlign="center" p="8px" borderRadius="8px" bg="rgba(217,119,6,0.06)" border="1px solid rgba(217,119,6,0.16)">
                {c}
              </Box>
            ))}
          </SimpleGrid>
          <Flex gap="10px" mt="14px" wrap="wrap">
            <Button leftIcon={<FiCopy />} variant="outline" borderColor="primary" color="primary" borderRadius="12px" fontWeight="700" onClick={copyAll} _hover={{ bg: "rgba(67,0,152,0.05)" }}>
              Kopiuj wszystkie
            </Button>
            <Button leftIcon={<FiDownload />} variant="outline" borderColor="primary" color="primary" borderRadius="12px" fontWeight="700" onClick={download} _hover={{ bg: "rgba(67,0,152,0.05)" }}>
              Pobierz .txt
            </Button>
          </Flex>
        </Box>
      ) : remaining === null ? (
        <Spinner size="sm" color={ACCENT} mt="14px" />
      ) : (
        <Button mt="16px" leftIcon={<FiKey />} variant={generated ? "outline" : "solid"} bg={generated ? "transparent" : ACCENT} color={generated ? ACCENT : "white"} borderColor={ACCENT} borderRadius="12px" fontWeight="700" isLoading={busy} onClick={generate} _hover={{ bg: generated ? "rgba(217,119,6,0.06)" : "#B45309" }}>
          {generated ? "Wygeneruj nowe kody" : "Wygeneruj kody zapasowe"}
        </Button>
      )}
    </SecurityCard>
  );
};
